import type { ComponentType, ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { cn } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { CardContentWrapper } from "~/ui/components/cardContent.wrapper";
import { IcHighPriority, IcTaskList, BrandIcWasteField } from "@racoon/icons";
import type { ConnectProto } from "@racoon/api";
import {
  buildDisposalSiteColors,
  DISPOSAL_SITE_PALETTE,
  type PlanFactOrganizationSummary,
} from "./analytics-report.shared";

type OverviewLegendEntry = {
  id: string;
  labelKey?: string;
  labelFallback?: string;
  value: number;
  color: string;
  bg?: boolean;
};

export type OverviewViewId = "landfills" | "plan-fact" | "anomalies";

type OverviewTabConfig = {
  id: OverviewViewId;
  Icon: ComponentType<{ className?: string }>;
  labelKey: string;
  labelFallback: string;
  iconColor?: string;
};

const OVERVIEW_TAB_CONFIG: OverviewTabConfig[] = [
  {
    id: "landfills",
    Icon: BrandIcWasteField,
    labelKey: "analytics.landfills",
    labelFallback: "Посещение полигонов",
  },
  {
    id: "plan-fact",
    iconColor: "text-brand-blue",
    Icon: IcTaskList,
    labelKey: "analytics.planAndFact",
    labelFallback: "План и факт вывоза",
  },
  {
    id: "anomalies",
    iconColor: "text-brand-red",
    Icon: IcHighPriority,
    labelKey: "analytics.anomalies",
    labelFallback: "Аномалии",
  },
];

const PLAN_FACT_LEGEND_DEF = [
  {
    id: "plan",
    value: 52,
    color: "#32ADE6",
    labelKey: "analytics.planAndFact.plan",
    labelFallback: "Запланировано площадок",
  },
  {
    id: "fact",
    value: 38,
    color: "#34C759",
    labelKey: "analytics.planAndFact.fact",
    labelFallback: "Вывезено площадок",
  },
  {
    id: "deviation",
    value: 10,
    color: "#FF3B30",
    labelKey: "analytics.planAndFact.deviation",
    labelFallback: "Невывезено площадок",
  },
] as const;

const ANOMALIES_LEGEND_DEF = [
  {
    id: "missing",
    value: 15,
    color: "#FF9500",
    labelKey: "analytics.anomalies.missing",
    labelFallback: "Отсутствие на полигоне за сутки",
  },
  {
    id: "stops",
    value: 7,
    color: "#FF3B30",
    labelKey: "analytics.anomalies.stops",
    labelFallback: "Остановка на Подозрительном участке",
  },
] as const;

export const AllMvosAnalyticsOverview = ({
  disposalVisitPerProvider,
  planFactSummaryByOrganization,
  anomalyPerProvider,
  isDisposalOverviewFetching,
  isPlanFactOverviewFetching,
  isAnomaliesOverviewFetching,
  selectedView,
  onSelectedViewChange,
}: {
  disposalVisitPerProvider: ConnectProto.GovAnalyticsDisposalVisitPerProvider[];
  planFactSummaryByOrganization: PlanFactOrganizationSummary[];
  anomalyPerProvider: ConnectProto.GovAnalyticsAnomalyPerProvider[];
  isDisposalOverviewFetching: boolean;
  isPlanFactOverviewFetching: boolean;
  isAnomaliesOverviewFetching: boolean;
  selectedView: OverviewViewId;
  onSelectedViewChange: (view: OverviewViewId) => void;
}) => {
  const t = useT();
  const setSelectedView = onSelectedViewChange;

  const landfillsLegend = useMemo(() => {
    const disposalSites = new Map<string, OverviewLegendEntry>();
    const colorBySiteId = buildDisposalSiteColors(disposalVisitPerProvider);

    disposalVisitPerProvider.forEach((provider) => {
      provider.disposalVisits?.forEach((visit) => {
        const disposalSiteId = String(visit.disposalSiteId ?? "");
        if (!disposalSiteId) return;

        const previous = disposalSites.get(disposalSiteId);
        disposalSites.set(disposalSiteId, {
          id: disposalSiteId,
          labelKey: undefined,
          labelFallback: visit.disposalSiteName ?? previous?.labelFallback ?? disposalSiteId,
          value: (previous?.value ?? 0) + Number(visit.totalVisits ?? 0),
          color: colorBySiteId.get(disposalSiteId) ?? DISPOSAL_SITE_PALETTE[0],
        });
      });
    });

    return Array.from(disposalSites.values());
  }, [disposalVisitPerProvider]);

  const selectedLegend =
    selectedView === "landfills"
      ? landfillsLegend
      : selectedView === "plan-fact"
        ? [...PLAN_FACT_LEGEND_DEF]
        : [...ANOMALIES_LEGEND_DEF];

  const hasNoOverviewData = useMemo(() => {
    switch (selectedView) {
      case "landfills":
        return disposalVisitPerProvider.length === 0 && !isDisposalOverviewFetching;
      case "plan-fact":
        return planFactSummaryByOrganization.length === 0 && !isPlanFactOverviewFetching;
      case "anomalies":
        return anomalyPerProvider.length === 0 && !isAnomaliesOverviewFetching;
      default:
        return false;
    }
  }, [
    selectedView,
    disposalVisitPerProvider,
    planFactSummaryByOrganization,
    anomalyPerProvider,
    isDisposalOverviewFetching,
    isPlanFactOverviewFetching,
    isAnomaliesOverviewFetching,
  ]);

  const renderOverviewCards = useCallback((): ReactNode => {
    switch (selectedView) {
      case "landfills":
        return disposalVisitPerProvider.map((provider, index) => {
          const visitsByDisposalSiteId = new Map(
            (provider.disposalVisits ?? []).map((visit) => [
              String(visit.disposalSiteId ?? ""),
              Number(visit.totalVisits ?? 0),
            ]),
          );
          const values = landfillsLegend.map(({ id, color }) => ({
            value: visitsByDisposalSiteId.get(id) ?? 0,
            color,
          }));
          const totalVisits = values.reduce((sum, item) => sum + item.value, 0);

          return (
            <OverviewSummaryCard
              key={provider.organizationId?.toString() ?? provider.organizationName ?? index}
              label={provider.organizationName ?? "—"}
              index={index + 1}
              values={values}
              count={totalVisits}
            />
          );
        });
      case "plan-fact": {
        const [planLegend, factLegend, failLegend] = [...PLAN_FACT_LEGEND_DEF];
        return planFactSummaryByOrganization.map((organization, index) => {
          const values = [
            {
              value: Number(organization.totals.stands_plan ?? 0),
              color: planLegend.color,
            },
            {
              value: Number(organization.totals.stands_visited ?? 0),
              color: factLegend.color,
            },
            {
              value: Number(organization.totals.stands_not_visited ?? 0),
              color: failLegend.color,
            },
          ];

          return (
            <OverviewSummaryCard
              key={organization.ownerOrganizationId ?? organization.organizationName ?? index}
              label={organization.organizationName ?? "—"}
              index={index + 1}
              values={values}
            />
          );
        });
      }
      case "anomalies": {
        const [dailyDisposalFailLegend, anomalyStopLegend] = [...ANOMALIES_LEGEND_DEF];
        return anomalyPerProvider.map((provider, index) => {
          const values = [
            {
              value: Number(provider.dailyDisposalFail ?? 0),
              color: dailyDisposalFailLegend.color,
            },
            {
              // value: Number(provider.anomalyStop ?? 0), // INFO: Kuna asked to always show 0
              value: 0,
              color: anomalyStopLegend.color,
            },
          ];

          return (
            <OverviewSummaryCard
              key={provider.organizationId?.toString() ?? provider.organizationName ?? index}
              label={provider.organizationName ?? "—"}
              index={index + 1}
              values={values}
              count={values.reduce((sum, item) => sum + item.value, 0)}
            />
          );
        });
      }
      default:
        return null;
    }
  }, [
    selectedView,
    disposalVisitPerProvider,
    planFactSummaryByOrganization,
    anomalyPerProvider,
    landfillsLegend,
  ]);

  return (
    <div className="w-full flex flex-col gap-2">
      <CardContentWrapper
        hasDivider={false}
        className="overflow-hidden rounded-lg border-slate-200"
      >
        <div className="flex w-full items-stretch">
          {OVERVIEW_TAB_CONFIG.map(({ id, labelKey, labelFallback, Icon, iconColor }) => {
            return (
              <div
                key={id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedView(id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedView(id);
                  }
                }}
                className="flex min-h-[48px] min-w-0 flex-1 flex-col items-center justify-between pt-3"
              >
                <div className="flex items-center gap-2 px-2">
                  <Icon className={cn("h-6 w-6 shrink-0", iconColor)} />
                  <span className="text-[12px] font-medium leading-[12px] tracking-[-0.12px] text-[#1D1F23]">
                    {t(Namespace.Govdash, labelKey as any) || labelFallback}
                  </span>
                </div>
                <div
                  className={cn(
                    "h-1 w-full",
                    selectedView === id ? "bg-[#007AFF]" : "bg-transparent",
                  )}
                />
              </div>
            );
          })}
        </div>
      </CardContentWrapper>
      {hasNoOverviewData ? (
        <CardContentWrapper
          hasDivider={false}
          className="flex min-h-[88px] items-center justify-center rounded-lg border-slate-200 p-4"
        >
          <p className="text-center text-xs font-medium leading-[12px] tracking-[-0.12px] text-[#1D1F23]">
            {t(Namespace.Common, "noData") || "Нет данных"}
          </p>
        </CardContentWrapper>
      ) : (
        <>
          <CardContentWrapper
            hasDivider={false}
            className="flex items-center justify-evenly p-2 rounded-lg"
          >
            {selectedLegend.map((legend) => (
              <div
                key={legend.id}
                className="flex-1 flex flex-col items-center justify-center gap-2"
              >
                <p className="text-xs font-medium leading-[12px] tracking-[-0.12px] text-[#1D1F23] text-center">
                  {t(Namespace.Govdash, legend.labelKey as any) || legend.labelFallback}
                </p>
                <span
                  className="h-2 w-4 shrink-0 rounded-3xl"
                  style={{ backgroundColor: legend.color }}
                />
              </div>
            ))}
          </CardContentWrapper>

          {renderOverviewCards()}
        </>
      )}
    </div>
  );
};

type OverviewSummaryCardProps = {
  label: string;
  index: number;
  count?: number;
  values: Array<{
    value: number;
    color: string;
    bg?: boolean;
  }>;
};

/** Single summary block under the legend row (mobile analytics card). */
function OverviewSummaryCard({ label, index, count, values }: OverviewSummaryCardProps) {
  return (
    <CardContentWrapper className="overflow-hidden rounded-lg border-slate-200">
      <div className="flex-1 flex flex-col items-center justify-center gap-2 p-2">
        <div className="flex items-center w-full">
          {values.map(({ value, color, bg }, valueIndex) => (
            <div
              key={`${value}-${valueIndex}`}
              className="flex items-center gap-1 flex-1 justify-center"
            >
              <span
                className={cn(
                  "rounded-lg border px-2 py-1 text-xs font-semibold leading-[12px] tracking-[-0.12px]",
                  bg ? "text-white" : "text-surface-invert",
                )}
                style={{ borderColor: color, backgroundColor: bg ? color : undefined }}
              >
                {value}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center p-2 justify-between">
        <div className="flex gap-2 items-center">
          <p className="text-[11px] font-medium leading-4 tracking-[-0.11px] text-slate-500">
            {index}
          </p>
          <p className="text-xs font-medium leading-[12px] tracking-[-0.12px] text-surface-invert">
            {label}
          </p>
        </div>
        {count != null && (
          <p className="min-w-6 text-center text-[9px] font-semibold p-1 rounded-lg leading-[9px] tracking-[-0.09px] text-surface-invert">
            {count}
          </p>
        )}
        {/* INFO: commented out truck info (active GPS pinged unique trucks (that visited one of the disposal sites) out of total trucks) 
        due to lack of API data */}
        {/* <div className="flex gap-2 items-center">
          <IcCar className="text-brand-blue h-6 w-6" />
          <p className="text-[9px] font-semibold p-2 rounded-lg border border-surface-invert leading-[9px] tracking-[-0.09px] text-surface-invert">
            11 из 14
          </p>
        </div> */}
      </div>
    </CardContentWrapper>
  );
}
